import { useEffect, useRef, useState } from "react";
import { onSnapshot } from "firebase/firestore";

const toRows = (snapshot, extra) =>
  snapshot.docs.map((d) => ({ id: d.id, ...d.data(), ...extra }));

// Live Firestore query. `key` identifies the query: the listener is
// re-created only when it changes, and a falsy key means "no query".
// `extra` fields are merged into every row (e.g. { product }).
export function useLiveQuery(key, buildQuery, extra) {
  const build = useRef(buildQuery);
  build.current = buildQuery;
  const extraRef = useRef(extra);
  extraRef.current = extra;
  const [state, setState] = useState({ data: [], loading: !!key, error: null });

  useEffect(() => {
    if (!key) {
      setState({ data: [], loading: false, error: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    return onSnapshot(
      build.current(),
      (snapshot) =>
        setState({
          data: toRows(snapshot, extraRef.current),
          loading: false,
          error: null,
        }),
      (error) => {
        console.error(`Live query "${key}" failed`, error);
        setState({ data: [], loading: false, error });
      },
    );
  }, [key]);

  return state;
}

// Live single document; data is null while loading or when it doesn't exist.
export function useLiveDoc(key, buildRef) {
  const build = useRef(buildRef);
  build.current = buildRef;
  const [state, setState] = useState({
    data: null,
    loading: !!key,
    error: null,
  });

  useEffect(() => {
    if (!key) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    return onSnapshot(
      build.current(),
      (snapshot) =>
        setState({
          data: snapshot.exists()
            ? { id: snapshot.id, ...snapshot.data() }
            : null,
          loading: false,
          error: null,
        }),
      (error) => {
        console.error(`Live doc "${key}" failed`, error);
        setState({ data: null, loading: false, error });
      },
    );
  }, [key]);

  return state;
}
